import type { SocketEventHandler, SocketEventMap } from "@/types/socket-events";

type StoredHandler = SocketEventHandler<keyof SocketEventMap>;

class SocketEmitter {
  private handlers = new Map<keyof SocketEventMap, Set<StoredHandler>>();

  on<K extends keyof SocketEventMap>(event: K, handler: SocketEventHandler<K>): () => void {
    const handlers = this.handlers.get(event) ?? new Set<StoredHandler>();
    handlers.add(handler as StoredHandler);
    this.handlers.set(event, handlers);

    return () => this.off(event, handler);
  }

  off<K extends keyof SocketEventMap>(event: K, handler: SocketEventHandler<K>): void {
    this.handlers.get(event)?.delete(handler as StoredHandler);
  }

  emit<K extends keyof SocketEventMap>(event: K, payload: SocketEventMap[K]): void {
    this.handlers.get(event)?.forEach((handler) => handler(payload));
  }

  async emitAsync<K extends keyof SocketEventMap>(event: K, payload: SocketEventMap[K]): Promise<boolean> {
    const handlers = [...(this.handlers.get(event) ?? [])];
    const results = await Promise.allSettled(handlers.map((handler) => handler(payload)));

    return results.every((result) => result.status === "fulfilled" && result.value !== false);
  }
}

export const socketEmitter = new SocketEmitter();
